
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from '@/integrations/supabase/client';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Plus, Users, Settings, UserPlus, Trophy, ArrowLeft, Search } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface Team {
  team_id: number;
  team_name: string;
  slug: string;
  team_description: string | null;
  sport_type: string | null;
  location: string | null;
  team_logo: string | null;
  created_at: string;
  member_count?: number;
}

interface TeamMember {
  id: string;
  user_id: string;
  username: string;
  first_name: string | null;
  last_name: string | null;
  role: string;
  profile_photo_url: string | null;
}

const TeamManagement: React.FC = () => {
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  
  const [newTeamData, setNewTeamData] = useState({
    team_name: '',
    team_description: '',
    sport_type: '',
    location: '',
  });

  const currentUserId = internalUser?.id || supabaseUser?.id;

  useEffect(() => {
    if (!currentUserId) {
      navigate('/login');
      return;
    }
    loadTeams();
  }, [currentUserId]);

  const loadTeams = async () => {
    if (!currentUserId) return;
    
    setLoading(true);
    try {
      // Fetch connected teams from user profile
      const { data: profileData } = await supabase
        .from('internal_user_profiles')
        .select('connected_teams')
        .eq('user_id', currentUserId)
        .single();

      const connectedTeamIds = (profileData?.connected_teams as number[]) || [];

      let teamsData: Team[] = [];
      if (connectedTeamIds.length > 0) {
        const { data, error } = await supabase
          .from('teams')
          .select('*')
          .in('team_id', connectedTeamIds)
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        teamsData = (data || []) as Team[];
      }

      // Get member counts for each team
      const teamsWithCounts = await Promise.all(
        teamsData.map(async (team) => {
          const { count: playerCount } = await supabase
            .from('team_players')
            .select('*', { count: 'exact', head: true })
            .eq('team_id', team.team_id);

          const { count: coachCount } = await supabase
            .from('team_coaches')
            .select('*', { count: 'exact', head: true })
            .eq('team_id', team.team_id);

          return {
            ...team,
            member_count: (playerCount || 0) + (coachCount || 0),
          };
        })
      );

      setTeams(teamsWithCounts);
    } catch (error) {
      console.error('Error loading teams:', error);
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async () => {
    if (!newTeamData.team_name.trim()) {
      toast.error('Team name is required');
      return;
    }

    setCreating(true);
    try {
      const slug = newTeamData.team_name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      const { data, error } = await supabase
        .from('teams')
        .insert({
          team_name: newTeamData.team_name,
          team_description: newTeamData.team_description || null,
          sport_type: newTeamData.sport_type || null,
          location: newTeamData.location || null,
          slug: `${slug}-${Date.now()}`,
          created_by: currentUserId,
        })
        .select()
        .single();

      if (error) throw error;

      toast.success('Team created successfully!');
      setCreateDialogOpen(false);
      setNewTeamData({ team_name: '', team_description: '', sport_type: '', location: '' });
      loadTeams();
    } catch (error) {
      console.error('Error creating team:', error);
      toast.error('Failed to create team');
    } finally {
      setCreating(false);
    }
  };

  const loadTeamMembers = async (team: Team) => {
    setSelectedTeam(team);
    setMembersLoading(true);
    try {
      // Load players
      const { data: players, error: playersError } = await supabase
        .from('team_players')
        .select(`
          player_id,
          internal_user_profiles!team_players_player_id_fkey (
            user_id,
            username,
            first_name,
            last_name,
            profile_photo_url
          )
        `)
        .eq('team_id', team.team_id);

      // Load coaches
      const { data: coaches, error: coachesError } = await supabase
        .from('team_coaches')
        .select(`
          coach_id,
          internal_user_profiles!team_coaches_coach_id_fkey (
            user_id,
            username,
            first_name,
            last_name,
            profile_photo_url
          )
        `)
        .eq('team_id', team.team_id);

      const members: TeamMember[] = [];

      // Process players
      if (players) {
        players.forEach((p: any) => {
          if (p.internal_user_profiles) {
            members.push({
              id: p.player_id,
              user_id: p.internal_user_profiles.user_id,
              username: p.internal_user_profiles.username || 'Unknown',
              first_name: p.internal_user_profiles.first_name,
              last_name: p.internal_user_profiles.last_name,
              role: 'player',
              profile_photo_url: p.internal_user_profiles.profile_photo_url,
            });
          }
        });
      }

      // Process coaches
      if (coaches) {
        coaches.forEach((c: any) => {
          if (c.internal_user_profiles) {
            members.push({
              id: c.coach_id,
              user_id: c.internal_user_profiles.user_id,
              username: c.internal_user_profiles.username || 'Unknown',
              first_name: c.internal_user_profiles.first_name,
              last_name: c.internal_user_profiles.last_name,
              role: 'coach',
              profile_photo_url: c.internal_user_profiles.profile_photo_url,
            });
          }
        });
      }

      setTeamMembers(members);
    } catch (error) {
      console.error('Error loading team members:', error);
      toast.error('Failed to load team members');
    } finally {
      setMembersLoading(false);
    }
  };

  const searchUsers = async () => {
    if (!searchQuery.trim()) return;

    setSearching(true);
    try {
      // Search in internal_user_profiles
      const { data: internalUsers, error: internalError } = await supabase
        .from('internal_user_profiles')
        .select('user_id, username, first_name, last_name, role, profile_photo_url')
        .or(`username.ilike.%${searchQuery}%,first_name.ilike.%${searchQuery}%,last_name.ilike.%${searchQuery}%`)
        .limit(10);

      if (internalError) throw internalError;

      // Filter out current user and already added members
      const memberIds = teamMembers.map(m => m.user_id);
      const filtered = (internalUsers || []).filter(
        u => u.user_id !== currentUserId && !memberIds.includes(u.user_id)
      );

      setSearchResults(filtered);
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error('Failed to search users');
    } finally {
      setSearching(false);
    }
  };

  const addMemberToTeam = async (userId: string, role: 'player' | 'coach') => {
    if (!selectedTeam) return;

    setAddingMember(true);
    try {
      const table = role === 'player' ? 'team_players' : 'team_coaches';
      const idField = role === 'player' ? 'player_id' : 'coach_id';

      if (role === 'player') {
        const { error } = await supabase
          .from('team_players')
          .insert({
            team_id: selectedTeam.team_id,
            user_id: userId,
          });
        if (error) {
          if (error.code === '23505') {
            toast.error('User is already a member of this team');
          } else {
            throw error;
          }
          return;
        }
      } else {
        const { error } = await supabase
          .from('team_coaches')
          .insert({
            team_id: selectedTeam.team_id,
            coach_id: userId,
          });
        if (error) {
          if (error.code === '23505') {
            toast.error('User is already a member of this team');
          } else {
            throw error;
          }
          return;
        }
      }

      toast.success(`${role.charAt(0).toUpperCase() + role.slice(1)} added to team!`);
      loadTeamMembers(selectedTeam);
      setSearchResults([]);
      setSearchQuery('');
    } catch (error) {
      console.error('Error adding member:', error);
      toast.error('Failed to add member to team');
    } finally {
      setAddingMember(false);
    }
  };

  const removeMemberFromTeam = async (userId: string, role: string) => {
    if (!selectedTeam) return;

    try {
      if (role === 'player') {
        const { error } = await supabase
          .from('team_players')
          .delete()
          .eq('team_id', selectedTeam.team_id)
          .eq('user_id', userId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('team_coaches')
          .delete()
          .eq('team_id', selectedTeam.team_id)
          .eq('coach_id', userId);
        if (error) throw error;
      }

      toast.success('Member removed from team');
      loadTeamMembers(selectedTeam);
    } catch (error) {
      console.error('Error removing member:', error);
      toast.error('Failed to remove member');
    }
  };

  const sportsOptions = [
    'Football', 'Basketball', 'Soccer', 'Baseball', 'Tennis',
    'Golf', 'Swimming', 'Track & Field', 'Volleyball', 'Hockey',
    'Softball', 'Wrestling', 'Lacrosse', 'Rugby', 'Cricket'
  ];

  if (!currentUserId) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {isMobile ? <MobileHeader /> : <Header />}
      
      <main className="container mx-auto px-4 py-8 pb-24 md:pb-8">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Team Management</h1>
              <p className="text-muted-foreground">Create and manage your teams</p>
            </div>
            
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-sports-green text-black hover:bg-green-600">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Team
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Create New Team</DialogTitle>
                  <DialogDescription>
                    Fill in the details to create a new team.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label htmlFor="team_name">Team Name *</Label>
                    <Input
                      id="team_name"
                      value={newTeamData.team_name}
                      onChange={(e) => setNewTeamData(prev => ({ ...prev, team_name: e.target.value }))}
                      placeholder="Enter team name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="sport_type">Sport</Label>
                    <Select
                      value={newTeamData.sport_type}
                      onValueChange={(value) => setNewTeamData(prev => ({ ...prev, sport_type: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select sport" />
                      </SelectTrigger>
                      <SelectContent>
                        {sportsOptions.map((sport) => (
                          <SelectItem key={sport} value={sport}>{sport}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      value={newTeamData.location}
                      onChange={(e) => setNewTeamData(prev => ({ ...prev, location: e.target.value }))}
                      placeholder="City, State"
                    />
                  </div>
                  <div>
                    <Label htmlFor="team_description">Description</Label>
                    <Textarea
                      id="team_description"
                      value={newTeamData.team_description}
                      onChange={(e) => setNewTeamData(prev => ({ ...prev, team_description: e.target.value }))}
                      placeholder="Describe your team..."
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleCreateTeam} 
                    disabled={creating}
                    className="bg-sports-green text-black hover:bg-green-600"
                  >
                    {creating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create Team'
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : teams.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Users className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Teams Yet</h3>
              <p className="text-muted-foreground mb-6">
                Create your first team to start managing players and coaches.
              </p>
              <Button 
                onClick={() => setCreateDialogOpen(true)}
                className="bg-sports-green text-black hover:bg-green-600"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Team
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Teams List */}
            <div className="lg:col-span-1 space-y-4">
              <h2 className="text-lg font-semibold text-foreground">Your Teams</h2>
              {teams.map((team) => (
                <Card 
                  key={team.team_id} 
                  className={`cursor-pointer transition-all hover:border-sports-green ${
                    selectedTeam?.team_id === team.team_id ? 'border-sports-green border-2' : ''
                  }`}
                  onClick={() => loadTeamMembers(team)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                        {team.team_logo ? (
                          <img src={team.team_logo} alt={team.team_name} className="w-full h-full object-cover rounded-lg" />
                        ) : (
                          <Trophy className="h-6 w-6 text-sports-green" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 
                          className="font-semibold text-foreground hover:text-sports-green hover:underline cursor-pointer transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/team/${team.slug}`);
                          }}
                        >
                          {team.team_name}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {team.sport_type || 'No sport'} • {team.member_count || 0} members
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Team Details */}
            <div className="lg:col-span-2">
              {selectedTeam ? (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Trophy className="h-5 w-5 text-sports-green" />
                      {selectedTeam.team_name}
                    </CardTitle>
                    <CardDescription>
                      {selectedTeam.team_description || 'No description'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Tabs defaultValue="members">
                      <TabsList className="mb-4">
                        <TabsTrigger value="members">Members</TabsTrigger>
                        <TabsTrigger value="add">Add Members</TabsTrigger>
                        <TabsTrigger value="settings">Settings</TabsTrigger>
                      </TabsList>

                      <TabsContent value="members">
                        {membersLoading ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin" />
                          </div>
                        ) : teamMembers.length === 0 ? (
                          <div className="text-center py-8">
                            <Users className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                            <p className="text-muted-foreground">No members yet. Add players and coaches to your team.</p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {teamMembers.map((member) => (
                              <div key={member.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-background rounded-full flex items-center justify-center overflow-hidden">
                                    {member.profile_photo_url ? (
                                      <img src={member.profile_photo_url} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                      <span className="text-sm font-medium">
                                        {(member.first_name?.[0] || member.username?.[0] || '?').toUpperCase()}
                                      </span>
                                    )}
                                  </div>
                                  <div>
                                    <p className="font-medium text-foreground">
                                      {member.first_name && member.last_name 
                                        ? `${member.first_name} ${member.last_name}`
                                        : member.username}
                                    </p>
                                    <p className="text-sm text-muted-foreground capitalize">{member.role}</p>
                                  </div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeMemberFromTeam(member.user_id, member.role)}
                                  className="text-destructive hover:text-destructive"
                                >
                                  Remove
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </TabsContent>

                      <TabsContent value="add">
                        <div className="space-y-4">
                          <div className="flex gap-2">
                            <Input
                              placeholder="Search users by username or name..."
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && searchUsers()}
                            />
                            <Button onClick={searchUsers} disabled={searching}>
                              {searching ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Search className="h-4 w-4" />
                              )}
                            </Button>
                          </div>

                          {searchResults.length > 0 && (
                            <div className="space-y-2">
                              {searchResults.map((user) => (
                                <div key={user.user_id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-background rounded-full flex items-center justify-center overflow-hidden">
                                      {user.profile_photo_url ? (
                                        <img src={user.profile_photo_url} alt="" className="w-full h-full object-cover" />
                                      ) : (
                                        <span className="text-sm font-medium">
                                          {(user.first_name?.[0] || user.username?.[0] || '?').toUpperCase()}
                                        </span>
                                      )}
                                    </div>
                                    <div>
                                      <p className="font-medium text-foreground">
                                        {user.first_name && user.last_name 
                                          ? `${user.first_name} ${user.last_name}`
                                          : user.username}
                                      </p>
                                      <p className="text-sm text-muted-foreground capitalize">{user.role || 'User'}</p>
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => addMemberToTeam(user.user_id, 'player')}
                                      disabled={addingMember}
                                    >
                                      <UserPlus className="h-4 w-4 mr-1" />
                                      Player
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => addMemberToTeam(user.user_id, 'coach')}
                                      disabled={addingMember}
                                    >
                                      <UserPlus className="h-4 w-4 mr-1" />
                                      Coach
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </TabsContent>

                      <TabsContent value="settings">
                        <div className="space-y-4">
                          <Button
                            variant="outline"
                            onClick={() => navigate(`/team/${selectedTeam.slug}`)}
                          >
                            View Team Page
                          </Button>
                        </div>
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>
              ) : (
                <Card className="h-full flex items-center justify-center">
                  <CardContent className="text-center py-12">
                    <Settings className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">Select a team to manage its members</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </main>

      {isMobile ? null : <Footer />}
    </div>
  );
};

export default TeamManagement;
